import classNames from 'classnames';
import Icon from '@components/atoms/icon';
import {
  getPriceChangeDirection,
  getPriceChangeTimeLabel
} from '@lib/my-team/price-change';
import type { PlayerPriceChange } from '@api-service/players';

const MAX_PROGRESS = 100;

export type PriceChangeTileProps = {
  priceChange?: PlayerPriceChange;
  isLoading?: boolean;
};

const RowShell = ({ children }: { children: React.ReactNode }) => (
  <div
    className="w-full bg-neutral-100 flex items-center justify-center px-2 h-[34px]"
    data-cy="pitch-player-price-change"
  >
    {children}
  </div>
);

/**
 * Pitch-tile price change row matching the FFH web Figma: a light grey strip
 * with the direction arrow and estimated change time on the left and a compact
 * progress pill on the right (blue track for a predicted rise, pink for a fall).
 */
const PriceChangeTile = ({ priceChange, isLoading }: PriceChangeTileProps) => {
  if (isLoading) {
    return (
      <RowShell>
        <span className="text-[10px] text-neutral-600 animate-pulse">…</span>
      </RowShell>
    );
  }

  const direction = priceChange
    ? getPriceChangeDirection(priceChange.progress)
    : 'none';

  if (!priceChange || direction === 'none') {
    return (
      <RowShell>
        <span className="text-xs font-semibold text-neutral-600">-</span>
      </RowShell>
    );
  }

  const isRise = direction === 'rise';
  const timeLabel = getPriceChangeTimeLabel(priceChange.estimatedChangeTime);
  const completed = Math.min(MAX_PROGRESS, Math.abs(priceChange.progress));

  return (
    <div
      className="w-full bg-neutral-100 flex items-center justify-between gap-1 px-2 h-[34px]"
      data-cy="pitch-player-price-change"
      data-cy-direction={direction}
    >
      <div className="flex items-center gap-1 min-w-0">
        <Icon
          name="ArrowUpRightFilled"
          className={classNames(
            'w-2.5 h-2.5 shrink-0',
            isRise ? 'text-success-700' : 'text-danger-600 rotate-90'
          )}
        />
        <span className="text-[10px] text-neutral-800 truncate">
          {timeLabel}
        </span>
      </div>
      <div className="relative h-5 w-[44px] shrink-0 rounded-lg overflow-hidden bg-white border border-neutral-200">
        <div
          className={classNames(
            'absolute inset-y-0',
            isRise ? 'left-0 bg-primary-100' : 'right-0 bg-danger-100'
          )}
          style={{ width: `${completed}%` }}
        />
        <span className="absolute inset-0 flex items-center justify-center text-[9px] font-normal text-neutral-800 leading-none">
          {priceChange.progress.toFixed(1)}%
        </span>
      </div>
    </div>
  );
};

export default PriceChangeTile;
